import { useState, useMemo, useEffect, useCallback } from "react";
import { fetchEvents, saveEvents, getCachedEvents } from "./s3Events";
import { useIsMobile } from "../useIsMobile";
import DatePicker from "./DatePicker";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CATEGORIES = ["Appointment","Birthday","School","Work","Family","Holiday","Reminder"];
const CAT_COLORS = {
  Appointment: "#E24B4A",
  Birthday: "#D4537E",
  School: "#378ADD",
  Work: "#7F77DD",
  Family: "#1D9E75",
  Holiday: "#EF9F27",
  Reminder: "#666",
};

function toStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function todayStr() { return toStr(new Date()); }

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const days = [];
  for (let i = 0; i < first.getDay(); i++) {
    days.push({ date: new Date(year, month, i - first.getDay() + 1), current: false });
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ date: new Date(year, month, d), current: true });
  }
  let next = 1;
  while (days.length < 42) {
    days.push({ date: new Date(year, month + 1, next++), current: false });
  }
  return days;
}

function fmt12(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function localDate(dateStr) {
  return new Date(dateStr + "T12:00:00");
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const C = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "24px 28px",
    maxWidth: 1280,
    margin: "0 auto",
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
  },
  calCard: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 12,
    padding: "22px 20px",
    flex: 1,
    minWidth: 0,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
  },
  monthTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 22,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    margin: 0,
  },
  navBtns: { display: "flex", gap: 6, alignItems: "center" },
  navBtn: {
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    color: "#b8b5ae",
    fontSize: 14,
    padding: "5px 12px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    lineHeight: 1,
  },
  todayBtn: {
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    color: "#b8b5ae",
    fontSize: 11,
    padding: "5px 12px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
  },
  addBtn: {
    background: "#1e1e22",
    border: "1px solid #3a3a3e",
    borderRadius: 7,
    color: "#f0ede6",
    fontSize: 11,
    padding: "6px 14px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
    marginLeft: 6,
  },
  dayHeaders: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
    marginBottom: 4,
  },
  dayHeader: {
    fontSize: 9,
    color: "#3a3a3e",
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    textAlign: "center",
    padding: "4px 0",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 2,
  },
  cell: (isToday, isSelected, isCurrent) => ({
    background: isSelected ? "#0f1a12" : isToday ? "#111122" : "#0f0f11",
    border: "1px solid " + (isSelected ? "#1D9E75" : isToday ? "#222244" : "#1a1a1d"),
    borderRadius: 6,
    padding: "6px 7px",
    minHeight: 78,
    cursor: "pointer",
    opacity: isCurrent ? 1 : 0.3,
    transition: "border-color 0.1s, background 0.1s",
  }),
  cellNum: (isToday, isSelected) => ({
    fontSize: 12,
    fontWeight: isToday ? 600 : 400,
    color: isSelected ? "#1D9E75" : isToday ? "#7777cc" : "#555",
    marginBottom: 4,
  }),
  eventBar: (color) => ({
    background: color + "22",
    borderLeft: `2px solid ${color}`,
    borderRadius: "0 3px 3px 0",
    padding: "1px 5px",
    fontSize: 9,
    color: color,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.6,
  }),
  moreText: { fontSize: 9, color: "#333", marginTop: 2, paddingLeft: 2 },
  // Side panel
  sidePanel: {
    width: 280,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  panelCard: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 12,
    padding: "18px 18px",
  },
  panelTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 15,
    fontWeight: 600,
    color: "#f0ede6",
    margin: 0,
    letterSpacing: "-0.01em",
  },
  panelSub: {
    fontSize: 9,
    color: "#3a3a3e",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginTop: 3,
    marginBottom: 14,
  },
  eventItem: {
    background: "#111113",
    border: "1px solid #1e1e22",
    borderRadius: 8,
    padding: "9px 11px",
    marginBottom: 7,
    cursor: "pointer",
    transition: "border-color 0.1s",
  },
  eventTitle: { fontSize: 12, color: "#e8e6e0", fontWeight: 500, marginBottom: 3 },
  eventMeta: { fontSize: 10, color: "#444" },
  catDot: (color) => ({
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: color,
    marginRight: 6,
    flexShrink: 0,
    verticalAlign: "middle",
  }),
  emptyState: { fontSize: 11, color: "#333", textAlign: "center", padding: "16px 0" },
  syncRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: "1px solid #1a1a1d",
  },
  syncText: { fontSize: 9, color: "#2a2a2e", letterSpacing: "0.06em" },
  refreshBtn: {
    background: "none",
    border: "none",
    color: "#333",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.06em",
    padding: 0,
  },
  // Legend
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px 10px",
    marginTop: 6,
  },
  legendItem: { display: "flex", alignItems: "center", fontSize: 9, color: "#555" },
  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 200,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  modal: {
    background: "#1a1a1d",
    border: "1px solid #2a2a2e",
    borderRadius: 14,
    padding: "28px",
    width: 420,
    maxHeight: "88vh",
    overflowY: "auto",
  },
  modalTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 18,
    fontWeight: 600,
    color: "#f0ede6",
    marginBottom: 20,
    margin: 0,
  },
  formGroup: { marginBottom: 14 },
  label: { display: "block", fontSize: 10, color: "#555", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 },
  input: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "9px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    outline: "none",
  },
  select: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "9px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    boxSizing: "border-box",
    outline: "none",
    cursor: "pointer",
  },
  btn: (variant) => ({
    fontFamily: "'DM Mono', monospace",
    fontSize: 11,
    padding: "8px 16px",
    borderRadius: 7,
    border: variant === "primary" ? "1px solid #3a3a3e" : variant === "danger" ? "1px solid #3a1212" : "1px solid #2a2a2e",
    background: variant === "primary" ? "#2a2a2e" : variant === "danger" ? "#1e0e0e" : "transparent",
    color: variant === "danger" ? "#E24B4A" : "#b8b5ae",
    cursor: "pointer",
    letterSpacing: "0.04em",
  }),
};

// ─── Event Modal ──────────────────────────────────────────────────────────────
function EventModal({ event, defaultDate, onSave, onClose, onDelete }) {
  const blank = { title: "", date: defaultDate || todayStr(), endDate: "", allDay: true, time: "", category: "Family", description: "" };
  const [form, setForm] = useState(event ? { endDate: "", ...event, allDay: event.allDay ?? true, time: event.time || "" } : blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function save() {
    if (!form.title.trim()) return;
    const endDate = form.endDate && form.endDate > form.date ? form.endDate : null;
    onSave({ ...form, endDate, id: event?.id ?? Date.now(), time: form.allDay ? null : (form.time || null) });
  }

  return (
    <div style={C.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={C.modal}>
        <p style={{ ...C.modalTitle, marginBottom: 20 }}>{event ? "Edit Event" : "New Event"}</p>

        <div style={C.formGroup}>
          <label style={C.label}>Title</label>
          <input style={C.input} value={form.title} onChange={e => set("title", e.target.value)} placeholder="Event title…" autoFocus />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={C.formGroup}>
            <label style={C.label}>Start Date</label>
            <DatePicker value={form.date} onChange={v => set("date", v)} />
          </div>
          <div style={C.formGroup}>
            <label style={C.label}>End Date (optional)</label>
            <DatePicker value={form.endDate} onChange={v => set("endDate", v)} />
          </div>
        </div>

        <div style={C.formGroup}>
          <label style={C.label}>Category</label>
          <select style={C.select} value={form.category} onChange={e => set("category", e.target.value)}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ ...C.formGroup, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" id="allDay" checked={form.allDay} onChange={e => set("allDay", e.target.checked)} />
          <label htmlFor="allDay" style={{ ...C.label, margin: 0, cursor: "pointer" }}>All Day</label>
        </div>

        {!form.allDay && (
          <div style={C.formGroup}>
            <label style={C.label}>Time</label>
            <input style={C.input} type="time" value={form.time} onChange={e => set("time", e.target.value)} />
          </div>
        )}

        <div style={C.formGroup}>
          <label style={C.label}>Description</label>
          <input style={C.input} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Optional notes…" />
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 20, alignItems: "center" }}>
          <button style={C.btn("primary")} onClick={save}>{event ? "Save" : "Add Event"}</button>
          <button style={C.btn()} onClick={onClose}>Cancel</button>
          {event && <button style={{ ...C.btn("danger"), marginLeft: "auto" }} onClick={() => onDelete(event.id)}>Delete</button>}
        </div>
      </div>
    </div>
  );
}

// ─── Main Calendar ────────────────────────────────────────────────────────────
export default function Calendar() {
  const isMobile = useIsMobile();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState(() => getCachedEvents() || []);
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(() => !(getCachedEvents()));
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchEvents();
      if (data !== null) setEvents(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      console.error("S3 load error:", e);
      setError(`S3 error: ${e.name} — ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [load]);

  const calendarDays = useMemo(() =>
    getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth]
  );

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      const end = ev.endDate && ev.endDate > ev.date ? ev.endDate : ev.date;
      let cur = new Date(ev.date + "T12:00:00");
      const endD = new Date(end + "T12:00:00");
      while (cur <= endD) {
        const ds = toStr(cur);
        (map[ds] = map[ds] || []).push(ev);
        cur = new Date(cur.getTime() + 86400000);
      }
    });
    return map;
  }, [events]);

  const selectedEvents = useMemo(() =>
    (eventsByDate[selectedDate] || []).slice().sort((a, b) => {
      if (a.allDay && !b.allDay) return -1;
      if (!a.allDay && b.allDay) return 1;
      return (a.time || "").localeCompare(b.time || "");
    }),
    [eventsByDate, selectedDate]
  );

  const upcomingEvents = useMemo(() => {
    const now = todayStr();
    const cutoff = toStr(new Date(Date.now() + 14 * 86400000));
    return events
      .filter(ev => ev.date >= now && ev.date <= cutoff)
      .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""));
  }, [events]);

  function prevMonth() { setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1)); }
  function nextMonth() { setCurrentMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1)); }
  function goToday() {
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(todayStr());
  }

  async function persistEvents(next) {
    setSaveStatus("saving");
    try {
      await saveEvents(next);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (e) {
      console.error("Save failed:", e);
      setSaveStatus(`Save failed: ${e.name} — ${e.message}`);
    }
  }

  async function saveEvent(ev) {
    const next = events.find(e => e.id === ev.id)
      ? events.map(e => e.id === ev.id ? ev : e)
      : [...events, ev];
    setEvents(next);
    setModal(null);
    await persistEvents(next);
  }

  async function deleteEvent(id) {
    const next = events.filter(e => e.id !== id);
    setEvents(next);
    setModal(null);
    await persistEvents(next);
  }

  const todayDateStr = todayStr();

  if (loading) return (
    <div style={{ ...C.root, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#333", fontSize: 12, letterSpacing: "0.1em" }}>Loading calendar…</p>
    </div>
  );

  if (error) return (
    <div style={{ ...C.root, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#E24B4A", fontSize: 12, maxWidth: 360, textAlign: "center" }}>{error}</p>
    </div>
  );

  return (
    <>
      <div style={C.root}>
        <div style={{ ...C.page, flexDirection: isMobile ? "column" : "row", padding: isMobile ? "16px 12px" : C.page.padding }}>

          {/* ── Calendar Grid ─────────────────────────────────────────── */}
          <div style={C.calCard}>
            <div style={C.header}>
              <p style={C.monthTitle}>{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}</p>
              <div style={C.navBtns}>
                <button style={C.navBtn} onClick={prevMonth}>‹</button>
                <button style={C.todayBtn} onClick={goToday}>Today</button>
                <button style={C.navBtn} onClick={nextMonth}>›</button>
                <button style={C.addBtn} onClick={() => setModal({ type: "add", date: selectedDate || todayDateStr })}>
                  + Event
                </button>
              </div>
            </div>

            <div style={C.dayHeaders}>
              {DAYS.map(d => <div key={d} style={C.dayHeader}>{d}</div>)}
            </div>

            <div style={C.grid}>
              {calendarDays.map(({ date, current }, i) => {
                const dateStr = toStr(date);
                const isToday = dateStr === todayDateStr;
                const isSelected = dateStr === selectedDate;
                const dayEvents = eventsByDate[dateStr] || [];
                return (
                  <div
                    key={i}
                    style={C.cell(isToday, isSelected, current)}
                    onClick={() => setSelectedDate(dateStr)}
                    onDoubleClick={() => setModal({ type: "add", date: dateStr })}
                  >
                    <div style={C.cellNum(isToday, isSelected)}>{date.getDate()}</div>
                    {dayEvents.slice(0, 2).map(ev => (
                      <div key={ev.id} style={C.eventBar(CAT_COLORS[ev.category] || "#555")}>
                        {ev.title}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div style={C.moreText}>+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ ...C.legend, marginTop: 14, paddingTop: 12, borderTop: "1px solid #1a1a1d" }}>
              {CATEGORIES.map(cat => (
                <div key={cat} style={C.legendItem}>
                  <span style={C.catDot(CAT_COLORS[cat])} />
                  {cat}
                </div>
              ))}
            </div>
          </div>

          {/* ── Side Panel ────────────────────────────────────────────── */}
          <div style={C.sidePanel}>

            {/* Selected day events */}
            <div style={C.panelCard}>
              <div style={C.syncRow}>
                <span style={{ ...C.syncText, color: saveStatus && saveStatus !== "saving" && saveStatus !== "saved" ? "#E24B4A" : saveStatus === "saved" ? "#1D9E75" : undefined }}>
                  {saveStatus === "saving" ? "Saving…"
                    : saveStatus === "saved" ? "Saved"
                    : saveStatus ? saveStatus
                    : lastUpdated ? `synced ${lastUpdated.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : ""}
                </span>
                <button style={C.refreshBtn} onClick={load}>↻ refresh</button>
              </div>

              <p style={C.panelTitle}>
                {localDate(selectedDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </p>
              <p style={C.panelSub}>{selectedEvents.length} event{selectedEvents.length !== 1 ? "s" : ""}</p>

              {selectedEvents.length === 0 ? (
                <p style={C.emptyState}>No events.<br /><span style={{ fontSize: 9, color: "#222" }}>Double-click a day to add.</span></p>
              ) : (
                selectedEvents.map(ev => (
                  <div key={ev.id} style={C.eventItem} onClick={() => setModal({ type: "edit", event: ev })}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={C.catDot(CAT_COLORS[ev.category] || "#555")} />
                      <span style={C.eventTitle}>{ev.title}</span>
                    </div>
                    <div style={C.eventMeta}>
                      {ev.endDate && ev.endDate > ev.date
                        ? `${localDate(ev.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${localDate(ev.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                        : ev.allDay ? "All day" : fmt12(ev.time)
                      } · {ev.category}
                      {ev.description && <div style={{ marginTop: 2 }}>{ev.description}</div>}
                    </div>
                  </div>
                ))
              )}

              <button
                style={{ ...C.addBtn, width: "100%", marginTop: 10, textAlign: "center", boxSizing: "border-box" }}
                onClick={() => setModal({ type: "add", date: selectedDate })}
              >
                + Add to This Day
              </button>
            </div>

            {/* Upcoming events */}
            <div style={C.panelCard}>
              <p style={C.panelTitle}>Upcoming</p>
              <p style={C.panelSub}>Next 14 days</p>
              {upcomingEvents.length === 0 ? (
                <p style={C.emptyState}>Nothing scheduled.</p>
              ) : (
                upcomingEvents.slice(0, 8).map(ev => (
                  <div key={ev.id} style={C.eventItem} onClick={() => setModal({ type: "edit", event: ev })}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span style={C.catDot(CAT_COLORS[ev.category] || "#555")} />
                      <span style={C.eventTitle}>{ev.title}</span>
                    </div>
                    <div style={C.eventMeta}>
                      {localDate(ev.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      {" · "}{ev.allDay ? "All day" : fmt12(ev.time)}
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      </div>

      {modal && (
        <EventModal
          event={modal.type === "edit" ? modal.event : null}
          defaultDate={modal.date}
          onSave={saveEvent}
          onClose={() => setModal(null)}
          onDelete={deleteEvent}
        />
      )}
    </>
  );
}
